import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useMemo, useState } from 'react';
import { FlatList, Modal, Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Animated, { FadeIn, FadeInDown, ZoomIn, useReducedMotion } from 'react-native-reanimated';

import { TeamCrest } from '@/components/matches/TeamCrest';
import { OutBadge } from '@/components/wc/OutBadge';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { useThemePalette } from '@/config/theme';
import { useMyAlbum, useOpenPack, useStickerPool } from '@/features/games/album';
import { useWallet } from '@/features/games/rewards';
import { useWcEliminatedTeams } from '@/features/wc/eliminations';
import { useAuth } from '@/lib/auth/AuthProvider';
import type { PlayerWithTeam, StickerGrant } from '@/lib/data/provider';
import { formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeTeamName } from '@/lib/i18n/footballNames';
import { errorKey } from '@/lib/utils/errors';

/** Coins charged by the backend for a paid pack (mirrors open_sticker_pack). */
const PACK_COST = 150;

/** A player at or above this fantasy price is a "star" — premium card + showcase.
 *  10.5 surfaces the genuine marquee names (Messi, Mbappé, Haaland, Salah, …). */
const STAR_PRICE = 10.5;

/** Warm-gold frame for star cards (the captain/gold accent, as a gradient). */
const GOLD = ['#F7D774', '#E7B13C', '#BE8A24'] as const;
const GOLD_INK = '#1A1408';

/** Whether a player is a marquee star (drives the gold frame + top showcase). */
function isStar(player: { fantasy_price?: number | string | null }): boolean {
  const price = typeof player.fantasy_price === 'string' ? Number(player.fantasy_price) : player.fantasy_price;
  return (price ?? 0) >= STAR_PRICE;
}

interface TeamGroup {
  teamId: string;
  teamName: string;
  logo: string | null;
  players: PlayerWithTeam[];
  owned: number;
  eliminated: boolean;
}

/** A premium gold star card for the top showcase. The marquee names are always
 *  named (even when uncollected) so the row reads as "legends to chase"; only
 *  the photo is gated behind owning the sticker. */
function StarTile({
  player,
  owned,
  eliminated,
}: {
  player: PlayerWithTeam;
  owned: boolean;
  eliminated: boolean;
}) {
  const palette = useThemePalette();
  const { locale } = useLocale();
  const name = player.known_as || player.name;

  return (
    <View style={{ width: 118 }}>
      <LinearGradient
        colors={GOLD}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={{ borderRadius: 18, padding: 2 }}
      >
        <View
          className="overflow-hidden rounded-2xl"
          style={{
            backgroundColor: palette.surface2,
            aspectRatio: 0.78,
            opacity: eliminated ? 0.5 : owned ? 1 : 0.92,
          }}
        >
          {/* Crown/star badge — the unmistakable "this is a star" marker. */}
          <View
            className="absolute end-1.5 top-1.5 z-10 h-6 w-6 items-center justify-center rounded-full"
            style={{ backgroundColor: GOLD_INK }}
          >
            <Ionicons name="star" size={12} color="#F7D774" />
          </View>
          {eliminated ? (
            <View className="absolute start-1.5 top-1.5 z-10">
              <OutBadge compact />
            </View>
          ) : null}
          {owned && player.photo_url ? (
            <Image
              source={{ uri: player.photo_url }}
              style={{ width: '100%', height: '72%' }}
              contentFit="cover"
              cachePolicy="memory-disk"
              transition={150}
            />
          ) : (
            <View className="h-[72%] w-full items-center justify-center" style={{ backgroundColor: palette.inset }}>
              <Ionicons name={owned ? 'person' : 'lock-closed'} size={24} color={palette.muted} />
            </View>
          )}
          <View
            className="h-[28%] w-full justify-center px-1.5"
            style={{ backgroundColor: owned ? palette.surface2 : palette.inset }}
          >
            <AppText variant="micro" weight="bold" numberOfLines={1} className="text-ink">
              {name}
            </AppText>
            <AppText variant="micro" numberOfLines={1} style={{ color: '#E7B13C' }}>
              {localizeTeamName(player.team, locale)}
            </AppText>
          </View>
        </View>
      </LinearGradient>
    </View>
  );
}

/** The top "Tournament Stars" showcase — a horizontal gold row of the marquee
 *  players, owned-aware. Distinctive look + distinctive placement. */
function StarShowcase({
  stars,
  ownedById,
  eliminatedSet,
}: {
  stars: PlayerWithTeam[];
  ownedById: Map<string, number>;
  eliminatedSet: Set<string>;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  if (stars.length === 0) return null;
  const ownedStars = stars.filter((p) => ownedById.has(p.id)).length;

  return (
    <View className="mb-5">
      <View className="mx-4 mb-2.5 flex-row items-center gap-2">
        <View
          className="h-7 w-7 items-center justify-center rounded-full"
          style={{ backgroundColor: 'rgba(231,177,60,0.16)' }}
        >
          <Ionicons name="star" size={15} color={palette.captain} />
        </View>
        <AppText variant="caption" weight="bold" className="flex-1 text-ink">
          {t('album.starsTitle')}
        </AppText>
        <AppText variant="micro" weight="bold" tabular style={{ color: '#E7B13C' }}>
          {/* Bidi-isolate the ratio so "3/12" can't reorder in Arabic RTL. */}
          {`⁦${formatNumber(ownedStars, numeralOptions)}/${formatNumber(stars.length, numeralOptions)}⁩`}
        </AppText>
      </View>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingHorizontal: 16, gap: 10 }}
      >
        {stars.map((player) => (
          <StarTile
            key={player.id}
            player={player}
            owned={ownedById.has(player.id)}
            eliminated={eliminatedSet.has(player.team?.id ?? '')}
          />
        ))}
      </ScrollView>
    </View>
  );
}

/** One sticker tile: revealed (photo + name) when owned, a dim silhouette when
 *  not. Stars get a gold frame + badge so they stand out inside the team grid. */
function StickerTile({
  player,
  owned,
  count,
  star,
}: {
  player: PlayerWithTeam;
  owned: boolean;
  count: number;
  star: boolean;
}) {
  const palette = useThemePalette();
  const { numeralOptions } = useLocale();
  const name = player.known_as || player.name;
  const borderColor = star ? '#E7B13C' : owned ? palette.voltLine : palette.line;

  return (
    <View className="mb-3 items-center" style={{ width: '31%' }}>
      <View
        className="w-full items-center justify-center overflow-hidden rounded-card"
        style={{
          aspectRatio: 0.82,
          backgroundColor: owned ? palette.surface2 : palette.inset,
          borderWidth: star ? 1.5 : 1,
          borderColor,
        }}
      >
        {star ? (
          <View
            className="absolute start-1 top-1 z-10 h-5 w-5 items-center justify-center rounded-full"
            style={{ backgroundColor: GOLD_INK }}
          >
            <Ionicons name="star" size={10} color="#F7D774" />
          </View>
        ) : null}
        {owned && player.photo_url ? (
          <Image
            source={{ uri: player.photo_url }}
            style={{ width: '100%', height: '78%' }}
            contentFit="cover"
            cachePolicy="memory-disk"
            transition={150}
          />
        ) : (
          <View className="h-[78%] w-full items-center justify-center">
            <Ionicons
              name={owned ? 'person' : 'help'}
              size={30}
              color={owned ? palette.muted : palette.line}
            />
          </View>
        )}
        <View className="h-[22%] w-full items-center justify-center px-1">
          <AppText
            variant="micro"
            weight={owned ? 'semibold' : 'medium'}
            numberOfLines={1}
            className={owned ? 'text-ink' : 'text-muted'}
          >
            {owned ? name : '???'}
          </AppText>
        </View>
        {/* Duplicate badge — the classic "got a double" marker. */}
        {owned && count > 1 ? (
          <View
            className="absolute end-1 top-1 rounded-full px-1.5 py-0.5"
            style={{ backgroundColor: palette.captain }}
          >
            <AppText variant="micro" weight="bold" tabular style={{ color: GOLD_INK }}>
              ×{formatNumber(count, numeralOptions)}
            </AppText>
          </View>
        ) : null}
      </View>
    </View>
  );
}

/** A thin progress bar with an inline owned/total caption. */
function ProgressBar({ owned, total }: { owned: number; total: number }) {
  const palette = useThemePalette();
  const { numeralOptions } = useLocale();
  const pct = total > 0 ? Math.min(100, Math.round((owned / total) * 100)) : 0;
  return (
    <View className="gap-1">
      <View className="h-1.5 overflow-hidden rounded-full" style={{ backgroundColor: palette.inset }}>
        <View style={{ width: `${pct}%`, height: '100%', backgroundColor: palette.volt }} />
      </View>
      <AppText variant="micro" weight="bold" tabular className="text-soft">
        {formatNumber(owned, numeralOptions)}/{formatNumber(total, numeralOptions)}
      </AppText>
    </View>
  );
}

/** The pack-open reveal overlay: animates the granted stickers, flagging NEW ones
 *  and giving star pulls a gold frame so they feel special. */
function RevealModal({
  grants,
  playersById,
  onClose,
}: {
  grants: StickerGrant[] | null;
  playersById: Map<string, PlayerWithTeam>;
  onClose: () => void;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { numeralOptions, locale } = useLocale();
  const reduce = useReducedMotion();

  const newCount = grants?.filter((g) => g.is_new).length ?? 0;

  return (
    <Modal visible={grants !== null} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        onPress={onClose}
        className="flex-1 items-center justify-center px-6"
        style={{ backgroundColor: 'rgba(2,6,14,0.86)' }}
      >
        <Animated.View
          entering={reduce ? undefined : FadeInDown.springify()}
          className="w-full max-w-[380px] rounded-card border border-volt-line bg-surface px-5 py-6"
        >
          <View className="mb-4 items-center gap-1">
            <View
              className="mb-1 h-12 w-12 items-center justify-center rounded-full"
              style={{ backgroundColor: palette.voltGlassStrong }}
            >
              <Ionicons name="sparkles" size={24} color={palette.voltBright} />
            </View>
            <AppText variant="heading" weight="bold" className="text-ink">
              {t('album.revealTitle')}
            </AppText>
            <AppText variant="caption" className="text-center text-soft">
              {newCount > 0
                ? t('album.revealNew', { count: formatNumber(newCount, numeralOptions) })
                : t('album.revealNone')}
            </AppText>
          </View>

          <View className="flex-row flex-wrap justify-center gap-2.5">
            {(grants ?? []).map((grant, i) => {
              const player = playersById.get(grant.player_id);
              const name = player ? player.known_as || player.name : '—';
              const star = player ? isStar(player) : false;
              return (
                <Animated.View
                  key={`${grant.player_id}-${i}`}
                  entering={reduce ? undefined : ZoomIn.delay(i * 90).springify()}
                  className="items-center"
                  style={{ width: 84 }}
                >
                  <View
                    className="w-full items-center justify-center overflow-hidden rounded-card"
                    style={{
                      aspectRatio: 0.82,
                      backgroundColor: palette.surface2,
                      borderWidth: star ? 2 : 1.5,
                      borderColor: star ? '#E7B13C' : grant.is_new ? palette.volt : palette.line,
                    }}
                  >
                    {star ? (
                      <View
                        className="absolute end-1 top-1 z-10 h-5 w-5 items-center justify-center rounded-full"
                        style={{ backgroundColor: GOLD_INK }}
                      >
                        <Ionicons name="star" size={10} color="#F7D774" />
                      </View>
                    ) : null}
                    {player?.photo_url ? (
                      <Image
                        source={{ uri: player.photo_url }}
                        style={{ width: '100%', height: '74%' }}
                        contentFit="cover"
                        cachePolicy="memory-disk"
                      />
                    ) : (
                      <View className="h-[74%] w-full items-center justify-center">
                        <Ionicons name="person" size={26} color={palette.muted} />
                      </View>
                    )}
                    <View className="h-[26%] w-full items-center justify-center px-1">
                      <AppText variant="micro" weight="semibold" numberOfLines={1} className="text-ink">
                        {name}
                      </AppText>
                    </View>
                    {grant.is_new ? (
                      <View
                        className="absolute start-1 top-1 rounded-full px-1.5 py-0.5"
                        style={{ backgroundColor: palette.volt }}
                      >
                        <AppText variant="micro" weight="bold" style={{ color: palette.onVolt }}>
                          {t('album.newSticker')}
                        </AppText>
                      </View>
                    ) : (
                      <View
                        className="absolute start-1 top-1 rounded-full px-1.5 py-0.5"
                        style={{ backgroundColor: palette.inset }}
                      >
                        <AppText variant="micro" weight="bold" className="text-muted">
                          {t('album.duplicate')}
                        </AppText>
                      </View>
                    )}
                  </View>
                  {player?.team ? (
                    <AppText variant="micro" numberOfLines={1} className="mt-1 text-center text-muted">
                      {localizeTeamName(player.team, locale)}
                    </AppText>
                  ) : null}
                </Animated.View>
              );
            })}
          </View>

          <Pressable
            accessibilityRole="button"
            onPress={onClose}
            className="mt-5 items-center py-2 active:opacity-70"
          >
            <AppText variant="caption" weight="bold" className="text-volt-bright">
              {t('album.tapToClose')}
            </AppText>
          </Pressable>
        </Animated.View>
      </Pressable>
    </Modal>
  );
}

export default function AlbumScreen() {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { numeralOptions, locale } = useLocale();
  const { userId } = useAuth();
  const toast = useToast();
  const reduce = useReducedMotion();

  const poolQ = useStickerPool();
  const albumQ = useMyAlbum();
  const walletQ = useWallet();
  const openPack = useOpenPack();
  const { set: eliminatedSet } = useWcEliminatedTeams();
  const [reveal, setReveal] = useState<StickerGrant[] | null>(null);

  const coins = walletQ.data?.coins ?? 0;
  const freeAvailable = albumQ.data?.free_available ?? false;
  const total = albumQ.data?.total ?? poolQ.data?.length ?? 0;

  // Owned player_id -> count, for the tile reveal + duplicate badge.
  const ownedById = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of albumQ.data?.owned ?? []) map.set(row.player_id, row.count);
    return map;
  }, [albumQ.data]);
  const ownedCount = ownedById.size;

  const playersById = useMemo(() => {
    const map = new Map<string, PlayerWithTeam>();
    for (const player of poolQ.data ?? []) map.set(player.id, player);
    return map;
  }, [poolQ.data]);

  // Marquee stars (price-ranked), surfaced in the top showcase row.
  const stars = useMemo(
    () =>
      (poolQ.data ?? [])
        .filter(isStar)
        .sort((a, b) => Number(b.fantasy_price ?? 0) - Number(a.fantasy_price ?? 0)),
    [poolQ.data],
  );

  // Group the pool by team. Within a team: stars first, then owned, then name —
  // so each team block leads with its big names and the collected ones.
  const groups = useMemo<TeamGroup[]>(() => {
    const byTeam = new Map<string, TeamGroup>();
    for (const player of poolQ.data ?? []) {
      const teamId = player.team?.id ?? 'none';
      let group = byTeam.get(teamId);
      if (!group) {
        group = {
          teamId,
          teamName: localizeTeamName(player.team, locale),
          logo: player.team?.logo_url ?? null,
          players: [],
          owned: 0,
          eliminated: eliminatedSet.has(teamId),
        };
        byTeam.set(teamId, group);
      }
      group.players.push(player);
      if (ownedById.has(player.id)) group.owned += 1;
    }
    const list = [...byTeam.values()];
    for (const group of list) {
      group.players.sort((a, b) => {
        const as = isStar(a) ? 1 : 0;
        const bs = isStar(b) ? 1 : 0;
        if (as !== bs) return bs - as;
        if (as === 1) return Number(b.fantasy_price ?? 0) - Number(a.fantasy_price ?? 0);
        const ao = ownedById.has(a.id) ? 1 : 0;
        const bo = ownedById.has(b.id) ? 1 : 0;
        if (ao !== bo) return bo - ao;
        return a.name.localeCompare(b.name);
      });
    }
    // Still-in teams first, then most-complete — keeps the live nations on top
    // while eliminated ones sink (but never disappear, so collections persist).
    return list.sort(
      (a, b) =>
        (a.eliminated ? 1 : 0) - (b.eliminated ? 1 : 0) ||
        b.owned - a.owned ||
        b.players.length - a.players.length,
    );
  }, [poolQ.data, ownedById, locale, eliminatedSet]);

  const onOpen = (kind: 'daily' | 'coins') => {
    if (!userId) {
      toast.show({ message: t('album.signIn'), tone: 'info' });
      return;
    }
    openPack.mutate(kind, {
      onSuccess: (result) => setReveal(result.granted),
      onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }),
    });
  };

  const completePct = total > 0 ? Math.round((ownedCount / total) * 100) : 0;
  const complete = total > 0 && ownedCount >= total;
  const loading = poolQ.isPending || (Boolean(userId) && albumQ.isPending);

  return (
    <Screen>
      <BackHeader title={t('album.title')} />
      <RevealModal grants={reveal} playersById={playersById} onClose={() => setReveal(null)} />
      {!userId ? (
        <EmptyState icon="albums-outline" title={t('album.title')} body={t('album.signIn')} />
      ) : poolQ.isError ? (
        <ErrorState error={poolQ.error} onRetry={() => poolQ.refetch()} />
      ) : (
        // Virtualized: each list item is ONE team block, so off-screen teams (and
        // their images) recycle instead of all mounting at once.
        <FlatList
          data={loading ? [] : groups}
          keyExtractor={(group) => group.teamId}
          contentContainerStyle={{ paddingBottom: 40 }}
          showsVerticalScrollIndicator={false}
          initialNumToRender={4}
          maxToRenderPerBatch={4}
          windowSize={7}
          removeClippedSubviews
          ListHeaderComponent={
            <>
              {/* Collection header: overall progress + the two pack buttons. */}
              <Animated.View entering={reduce ? undefined : FadeIn}>
                <Card className="mx-4 mb-4 mt-1 gap-4 px-4 py-4">
                  <View className="flex-row items-center gap-3">
                    <View
                      className="h-12 w-12 items-center justify-center rounded-2xl"
                      style={{ backgroundColor: palette.voltGlassStrong }}
                    >
                      <Ionicons name="albums" size={24} color={palette.voltBright} />
                    </View>
                    <View className="flex-1">
                      <View className="flex-row items-baseline gap-1.5">
                        <AppText variant="title" weight="bold" tabular className="text-ink">
                          {formatNumber(ownedCount, numeralOptions)}
                        </AppText>
                        <AppText variant="caption" className="text-muted">
                          / {formatNumber(total, numeralOptions)} {t('album.collected')}
                        </AppText>
                      </View>
                      <AppText variant="micro" className="text-soft">
                        {t('album.completePercent', { percent: formatNumber(completePct, numeralOptions) })}
                      </AppText>
                    </View>
                  </View>

                  <View
                    className="h-2 overflow-hidden rounded-full"
                    style={{ backgroundColor: palette.inset }}
                  >
                    <View
                      style={{ width: `${completePct}%`, height: '100%', backgroundColor: palette.volt }}
                    />
                  </View>

                  {complete ? (
                    <View className="flex-row items-center gap-2 rounded-card border border-volt-line bg-volt-glass px-3 py-2.5">
                      <Ionicons name="trophy" size={18} color={palette.captain} />
                      <View className="flex-1">
                        <AppText variant="caption" weight="bold" className="text-ink">
                          {t('album.completeTitle')}
                        </AppText>
                        <AppText variant="micro" className="text-muted">
                          {t('album.completeBody')}
                        </AppText>
                      </View>
                    </View>
                  ) : (
                    <View className="flex-row gap-2.5">
                      <View className="flex-1">
                        <Button
                          label={freeAvailable ? t('album.openDaily') : t('album.openDailyDone')}
                          variant="primary"
                          compact
                          disabled={!freeAvailable || openPack.isPending}
                          loading={openPack.isPending && openPack.variables === 'daily'}
                          onPress={() => onOpen('daily')}
                        />
                        <AppText variant="micro" className="mt-1 text-center text-muted">
                          {freeAvailable ? t('album.openDailyFree') : t('album.openDailyDone')}
                        </AppText>
                      </View>
                      <View className="flex-1">
                        <Button
                          label={t('album.openCoins')}
                          variant="secondary"
                          compact
                          disabled={coins < PACK_COST || openPack.isPending}
                          loading={openPack.isPending && openPack.variables === 'coins'}
                          onPress={() => onOpen('coins')}
                        />
                        <AppText variant="micro" className="mt-1 text-center text-muted">
                          {t('album.packCost', { coins: formatNumber(PACK_COST, numeralOptions) })}
                        </AppText>
                      </View>
                    </View>
                  )}
                </Card>
              </Animated.View>

              {!loading ? (
                <StarShowcase stars={stars} ownedById={ownedById} eliminatedSet={eliminatedSet} />
              ) : null}

              <AppText variant="caption" className="mx-4 mb-3 text-soft">
                {t('album.intro')}
              </AppText>
            </>
          }
          ListEmptyComponent={
            loading ? (
              <View className="gap-4 px-4">
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-44 w-full" />
                <Skeleton className="h-44 w-full" />
              </View>
            ) : (
              <EmptyState
                icon="albums-outline"
                title={t('album.emptyTitle')}
                body={t('album.emptyBody')}
              />
            )
          }
          renderItem={({ item: group, index: gi }) => (
            <Animated.View
              entering={reduce || gi > 6 ? undefined : FadeInDown.delay(gi * 50).springify()}
              className="mb-5"
              style={group.eliminated ? { opacity: 0.6 } : undefined}
            >
              <View className="mx-4 mb-2.5 flex-row items-center gap-2.5">
                <TeamCrest uri={group.logo} name={group.teamName} size="sm" />
                <AppText variant="caption" weight="bold" numberOfLines={1} className="flex-1 text-ink">
                  {group.teamName}
                </AppText>
                {group.eliminated ? <OutBadge /> : null}
                <View className="w-24">
                  <ProgressBar owned={group.owned} total={group.players.length} />
                </View>
              </View>
              <View className="flex-row flex-wrap justify-between px-4">
                {group.players.map((player) => (
                  <StickerTile
                    key={player.id}
                    player={player}
                    owned={ownedById.has(player.id)}
                    count={ownedById.get(player.id) ?? 0}
                    star={isStar(player)}
                  />
                ))}
              </View>
            </Animated.View>
          )}
        />
      )}
    </Screen>
  );
}
